/*
 * Copyright (c) 2022-2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import PropTypes from 'prop-types';
import React from 'react';
import Overlay from "../Overlay/Overlay.jsx";
import {AnimatePresence, motion} from "motion/react";

const frame = typeof window === 'undefined' ? undefined : window.document

export class FileDrop extends React.PureComponent {
    constructor(props) {
        super(props);

        this.frameDragCounter = 0;
        this.state = {draggingOverFrame: false, draggingOverTarget: false};
    }

    resetDragging = () => {
        this.frameDragCounter = 0;
        this.setState({draggingOverFrame: false, draggingOverTarget: false});
    }

    handleWindowDragOverOrDrop = (event) => {
        // This prevents the browser from trying to load whatever file the user dropped on the window
        event.preventDefault();
    }

    handleFrameDrag = (event) => {
        // Only allow dragging of files
        if (!this.eventHasFiles(event))
            return;
        // We are listening for events on the 'frame', so every time the user drags over any element in the frame's tree,
        // the event bubbles up to the frame. By keeping count of how many "dragenters" we get, we can tell if they are still
        // "draggingOverFrame" (b/c you get one "dragenter" initially, and one "dragenter"/one "dragleave" for every bubble)
        // This is far better than a "dragover" handler, which would be calling `setState` continuously.
        this.frameDragCounter += event.type === 'dragenter' ? 1 : -1;
        if (this.frameDragCounter === 1) {
            this.setState({draggingOverFrame: true});
            if (this.props.onFrameDragEnter)
                this.props.onFrameDragEnter(event);
            return;
        }
        if (this.frameDragCounter === 0) {
            this.setState({draggingOverFrame: false});
            if (this.props.onFrameDragLeave)
                this.props.onFrameDragLeave(event);

        }
    }

    handleFrameDrop = (event) => {
        if (!this.state.draggingOverTarget) {
            this.resetDragging();
            if (this.props.onFrameDrop)
                this.props.onFrameDrop(event);
        }
    }

    handleDragOver = (event) => {
        if (this.eventHasFiles(event)) {
            this.setState({draggingOverTarget: true});
            if (!this.isIE() && this.props.dropEffect)
                event.dataTransfer.dropEffect = this.props.dropEffect;
            if (this.props.onDragOver)
                this.props.onDragOver(event);
        }
    }

    handleDragLeave = (event) => {
        this.setState({draggingOverTarget: false});
        if (this.props.onDragLeave)
            this.props.onDragLeave(event);
    }

    handleDrop = (event) => {
        if (this.props.onDrop && this.eventHasFiles(event)) {
            const files = event.dataTransfer ? Array.from(event.dataTransfer.files) : null;
            this.props.onDrop(files, event);
        }
        this.resetDragging();
    };
    handleTargetClick = (event) => {
        if (this.props.onTargetClick) {
            this.props.onTargetClick(event);
        }
        this.resetDragging();
    };
    stopFrameListeners = (frame) => {
        if (frame) {
            frame.removeEventListener('dragenter', this.handleFrameDrag);
            frame.removeEventListener('dragleave', this.handleFrameDrag);
            frame.removeEventListener('drop', this.handleFrameDrop);
        }
    };
    startFrameListeners = (frame) => {
        if (frame) {
            frame.addEventListener('dragenter', this.handleFrameDrag);
            frame.addEventListener('dragleave', this.handleFrameDrag);
            frame.addEventListener('drop', this.handleFrameDrop);
        }
    };
    isIE = () => typeof window !== 'undefined' &&
        (window.navigator.userAgent.indexOf('MSIE') !== -1 ||
            window.navigator.appVersion.indexOf('Trident/') > 0);
    eventHasFiles = (event) => {
        // In most browsers this is an array, but in IE11 it's an Object :(
        let hasFiles = false;
        if (event.dataTransfer) {
            const types = event.dataTransfer.types;
            for (const keyOrIndex in types) {
                if (types[keyOrIndex] === 'Files') {
                    hasFiles = true;
                    break;
                }
            }
        }
        return hasFiles;
    };

    componentDidMount() {
        this.startFrameListeners(frame);
        this.resetDragging();
        window.addEventListener('dragover', this.handleWindowDragOverOrDrop);
        window.addEventListener('drop', this.handleWindowDragOverOrDrop);
    }


    componentWillUnmount() {
        this.stopFrameListeners(frame);
        window.removeEventListener('dragover', this.handleWindowDragOverOrDrop);
        window.removeEventListener('drop', this.handleWindowDragOverOrDrop);
    }

    render() {
        const {
            children,
            className,
            targetClassName,
        } = this.props;
        const {draggingOverTarget, draggingOverFrame} = this.state;

        let fileDropTargetClassName = targetClassName;

        return (
            <div
                onDragOver={this.handleDragOver}
                onDragLeave={this.handleDragLeave}
                onDrop={this.handleDrop}
                className={fileDropTargetClassName} onClick={this.handleTargetClick}
            >
                <Overlay
                    className={"pointer-events-none"}
                    overlay={<>
                        <AnimatePresence mode={"popLayout"}>
                            {(draggingOverTarget) && (<>
                                <motion.div
                                    key={"overlay"}
                                    initial={{scale: 0.3, y: 30, opacity: 0}}
                                    animate={{scale: 1, y: 0, opacity: 1}}
                                    exit={{scale: 0.3, opacity: 0}}
                                    className={`w-full h-full rounded-lg transition-colors duration-300  border-2 border-dashed border-blue-400 bg-blue-200/40!  pointer-events-none`}>


                                </motion.div>

                            </>)}
                        </AnimatePresence>


                    </>}>


                    {children}
                </Overlay>
            </div>
        );
    }
}


FileDrop.propTypes = {
    targetClassName: PropTypes.string,
    draggingOverFrameClassName: PropTypes.string,
    draggingOverTargetClassName: PropTypes.string,
    onDragOver: PropTypes.func,
    onDragLeave: PropTypes.func,
    onDrop: PropTypes.func,
    onTargetClick: PropTypes.func,
    dropEffect: PropTypes.oneOf(['copy', 'move', 'link', 'none']),
    onFrameDragEnter: PropTypes.func,
    onFrameDragLeave: PropTypes.func,
    onFrameDrop: PropTypes.func,
};
FileDrop.defaultProps = {
    dropEffect: 'copy',
    targetClassName: 'w-full h-full ',
};